import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const GoalSetting = () => {
  const [goals, setGoals] = useState([
    {
      id: 1,
      type: 'food',
      title: 'Comida Rescatada',
      target: 50,
      current: 34,
      unit: 'kg',
      icon: 'Utensils',
      color: 'var(--color-success)',
      deadline: '2024-12-31',
      isActive: true
    },
    {
      id: 2,
      type: 'streak',
      title: 'Días Consecutivos',
      target: 30,
      current: 12,
      unit: 'días',
      icon: 'Flame',
      color: 'var(--color-accent)',
      deadline: '2024-12-15',
      isActive: true
    },
    {
      id: 3,
      type: 'water',
      title: 'Agua Ahorrada',
      target: 1000,
      current: 510,
      unit: 'L',
      icon: 'Droplets',
      color: 'var(--color-primary)',
      deadline: '2024-12-31',
      isActive: false
    }
  ]);

  const [showNewGoalForm, setShowNewGoalForm] = useState(false);
  const [newGoal, setNewGoal] = useState({
    type: 'food',
    target: '',
    deadline: ''
  });

  const goalTypes = [
    { value: 'food', label: 'Comida Rescatada (kg)', icon: 'Utensils', color: 'var(--color-success)' },
    { value: 'streak', label: 'Días Consecutivos', icon: 'Flame', color: 'var(--color-accent)' },
    { value: 'water', label: 'Agua Ahorrada (L)', icon: 'Droplets', color: 'var(--color-primary)' },
    { value: 'co2', label: 'CO₂ Evitado (kg)', icon: 'Leaf', color: 'var(--color-secondary)' },
    { value: 'bags', label: 'Bolsas Rescatadas', icon: 'ShoppingBag', color: 'var(--color-warning)' }
  ];

  const getProgressPercentage = (current, target) => {
    return Math.min((current / target) * 100, 100);
  };

  const getDaysRemaining = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleCreateGoal = () => {
    if (!newGoal?.target || !newGoal?.deadline) return;

    const goalType = goalTypes?.find(type => type?.value === newGoal?.type);
    const newGoalObj = {
      id: Date.now(),
      type: newGoal?.type,
      title: goalType?.label?.split(' (')?.[0],
      target: parseInt(newGoal?.target),
      current: 0,
      unit: goalType?.label?.includes('(') ? goalType?.label?.split('(')?.[1]?.replace(')', '') : '',
      icon: goalType?.icon,
      color: goalType?.color,
      deadline: newGoal?.deadline,
      isActive: true
    };

    setGoals([...goals, newGoalObj]);
    setNewGoal({ type: 'food', target: '', deadline: '' });
    setShowNewGoalForm(false);
  };

  const toggleGoalActive = (goalId) => {
    setGoals(goals?.map(goal => 
      goal?.id === goalId ? { ...goal, isActive: !goal?.isActive } : goal
    ));
  };

  const deleteGoal = (goalId) => {
    setGoals(goals?.filter(goal => goal?.id !== goalId));
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading font-semibold text-lg text-foreground">
          Mis Objetivos Personales
        </h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowNewGoalForm(true)}
        >
          <Icon name="Plus" size={16} className="mr-2" />
          Nuevo Objetivo
        </Button>
      </div>
      {/* Active Goals */}
      <div className="space-y-4 mb-6">
        {goals?.filter(goal => goal?.isActive)?.map((goal) => {
          const progress = getProgressPercentage(goal?.current, goal?.target);
          const daysRemaining = getDaysRemaining(goal?.deadline);
          
          return (
            <div key={goal?.id} className="p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${goal?.color}15` }}>
                    <Icon name={goal?.icon} size={20} color={goal?.color} />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">{goal?.title}</h4>
                    <p className="text-sm text-muted-foreground">
                      {goal?.current} / {goal?.target} {goal?.unit}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => toggleGoalActive(goal?.id)}
                  >
                    <Icon name="Pause" size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteGoal(goal?.id)}
                  >
                    <Icon name="Trash2" size={16} />
                  </Button>
                </div>
              </div>
              {/* Progress Bar */}
              <div className="mb-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-muted-foreground">Progreso</span>
                  <span className="text-sm font-medium text-foreground">
                    {progress?.toFixed(0)}%
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="h-2 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${progress}%`,
                      backgroundColor: goal?.color
                    }}
                  ></div>
                </div>
              </div>
              {/* Goal Status */}
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={daysRemaining > 0 ? "Calendar" : "AlertTriangle"} 
                    size={14} 
                    color={daysRemaining > 0 ? "var(--color-muted-foreground)" : "var(--color-warning)"}
                  />
                  <span className={daysRemaining > 0 ? "text-muted-foreground" : "text-warning"}>
                    {daysRemaining > 0 ? `${daysRemaining} días restantes` : "¡Fecha límite pasada!"}
                  </span>
                </div>
                
                {progress >= 100 && (
                  <div className="flex items-center space-x-1 text-success">
                    <Icon name="CheckCircle" size={14} />
                    <span className="font-medium">¡Completado!</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Paused Goals */}
      {goals?.filter(goal => !goal?.isActive)?.length > 0 && (
        <div className="mb-6">
          <h4 className="font-medium text-muted-foreground mb-3 text-sm">Objetivos Pausados</h4>
          <div className="space-y-2">
            {goals?.filter(goal => !goal?.isActive)?.map((goal) => (
              <div key={goal?.id} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg opacity-60">
                <div className="flex items-center space-x-3">
                  <Icon name={goal?.icon} size={16} color="var(--color-muted-foreground)" />
                  <span className="text-sm text-muted-foreground">{goal?.title}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleGoalActive(goal?.id)}
                  >
                    <Icon name="Play" size={14} className="mr-1" />
                    Reanudar
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteGoal(goal?.id)}
                  >
                    <Icon name="Trash2" size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* New Goal Form Modal */}
      {showNewGoalForm && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-lg text-foreground">
                Crear Nuevo Objetivo
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowNewGoalForm(false)}
              >
                <Icon name="X" size={20} />
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Tipo de Objetivo
                </label>
                <select
                  value={newGoal?.type}
                  onChange={(e) => setNewGoal({ ...newGoal, type: e?.target?.value })}
                  className="w-full p-2 border border-border rounded-md bg-background text-foreground"
                >
                  {goalTypes?.map((type) => (
                    <option key={type?.value} value={type?.value}>
                      {type?.label}
                    </option>
                  ))}
                </select>
              </div>

              <Input
                label="Meta a Alcanzar"
                type="number"
                placeholder="Ej: 50"
                value={newGoal?.target}
                onChange={(e) => setNewGoal({ ...newGoal, target: e?.target?.value })}
                required
              />

              <Input
                label="Fecha Límite"
                type="date"
                value={newGoal?.deadline}
                onChange={(e) => setNewGoal({ ...newGoal, deadline: e?.target?.value })}
                required
              />
            </div>

            <div className="flex space-x-3 mt-6">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowNewGoalForm(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="default"
                className="flex-1"
                onClick={handleCreateGoal}
                disabled={!newGoal?.target || !newGoal?.deadline}
              >
                Crear Objetivo
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Motivational Message */}
      <div className="p-4 bg-gradient-to-r from-primary/10 to-success/10 rounded-lg border border-primary/20">
        <div className="flex items-start space-x-3">
          <Icon name="Target" size={20} color="var(--color-primary)" />
          <div>
            <h5 className="font-medium text-foreground text-sm mb-1">
              ¡Mantén el enfoque!
            </h5>
            <p className="text-xs text-muted-foreground">
              Los objetivos claros te ayudan a mantener la motivación y maximizar tu impacto ambiental. 
              Cada pequeño paso cuenta hacia un futuro más sostenible.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoalSetting;